import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { totalCheckboxSelected } from '@ubeya/shared-web/utils/checkbox';
import {
  NameRenderer,
  FiltersRow,
  Filter,
  FlexSpace,
  FlexColumn,
  Search,
  PaginationTable as BasePaginationTable,
  Tooltip,
  CheckBox
} from '@ubeya/shared-web/components';
import useDebounce from '@ubeya/shared/hooks/useDebounce';
import useLoadingWizard from '@ubeya/shared-web/hooks/useLoadingWizard';
import useEmployees from '@ubeya/shared/hooks/employees/useEmployees';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { applyFilter } from '@ubeya/shared-web/utils/filters';
import { FILTERS_IDS } from '@ubeya/shared/constants';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import EmployeeCard from '../EmployeeCard/EmployeeCard';
import EarningsLimitCheckBox, { applyEarningsLimitFilter, hasEarningsLimitFilter } from './EarningsLimitCheckBox';

const PublishCheckboxContainer = styled(FlexColumn)`
  margin: 10px 5px 5px;
`;

const PubishToPreferred = styled(FlexColumn)`
  margin: 5px 5px;
`;

const Header = styled(FlexSpace)`
  padding: 3px 30px 10px 30px;
  min-height: 59px;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
`;

const Container = styled(FlexColumn)`
  flex: 1;
`;

const PaginationTable = styled(BasePaginationTable)`
  .cell {
    border-right: none !important;
  }
  .cell:first-child {
    padding: 5px !important;
  }
`;

const EmployeesContent = ({
  isShift,
  params,
  checkboxData,
  filters = {},
  setFilters,
  disabledFilters,
  onTotalChange,
  allowCheckAvailableByTimeAvailability = true,
  allowCheckEarningsLimit = true,
  item = {}
}) => {
  const { t, isEmployeePreferredAreasEnabled } = useContext(ConfigContext);
  const { mappedLocations } = useLocations();
  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 300);
  const [selectedEmployee, setSelectedEmployee] = useState();

  const {
    config: { hasTimeAvailability }
  } = useAccount();

  const { projectDate, clientId } = params || {};

  const showCheckWithAvailableByTimeAvailability =
    isShift && hasTimeAvailability && allowCheckAvailableByTimeAvailability;

  const showEarningsLimit = isShift && allowCheckEarningsLimit && !!item.shiftId;

  const handleChangePublishToNonBooked = useCallback(
    (newValue) =>
      setFilters(
        applyFilter({
          type: 'basic',
          id: FILTERS_IDS.NOT_BOOKED_ON_DATE_FILTER_ID,
          value: newValue ? item.shiftDate || projectDate : null,
          allFilters: filters
        })
      ),
    [setFilters, item.shiftDate, projectDate, filters]
  );

  const handleChangeEarningsLimit = useCallback(
    (checked) => setFilters(applyEarningsLimitFilter({ filters, shiftId: item.shiftId, checked })),
    [setFilters, item.shiftId, filters]
  );

  const handleChangePublishToPreferredClient = useCallback(
    (newValue) =>
      setFilters(
        applyFilter({
          type: 'basic',
          id: FILTERS_IDS.PREFERRED_CLIENTS_FILTER_ID,
          value: newValue ? [clientId] : null,
          allFilters: filters
        })
      ),
    [setFilters, clientId, filters]
  );

  const handleChangeAvailableByTimeAvailability = useCallback(
    (newValue) =>
      setFilters(
        applyFilter({
          type: 'basic',
          id: FILTERS_IDS.AVAILABLE_BY_TIME_AVAILABILITY,
          value: newValue ? item.shiftId : null,
          allFilters: filters
        })
      ),
    [setFilters, item.shiftId, filters]
  );

  const handleChangeWorkedInAreaBefore = useCallback(
    (newValue) => {
      setFilters(
        applyFilter({
          type: 'basic',
          id: FILTERS_IDS.WORKED_IN_AREA_BEFORE,
          value: newValue && item?.locationId ? [item.locationId] : null,
          allFilters: filters
        })
      );
    },
    [filters, item.locationId, setFilters]
  );

  const handleChangePreferredArea = useCallback(
    (newValue) => {
      setFilters(
        applyFilter({
          type: 'basic',
          id: FILTERS_IDS.PREFERRED_AREA,
          value: newValue && item?.locationId ? [item.locationId] : null,
          allFilters: filters
        })
      );
    },
    [filters, item.locationId, setFilters]
  );

  const paramsWithSearch = useMemo(() => ({ ...params, pageSize: 100, search: debounceSearch, isShift }), [
    debounceSearch,
    params,
    isShift
  ]);

  const { page, setPage, pageSize, totalItems, data, isLoading } = useEmployees(paramsWithSearch);

  useLoadingWizard(isLoading);

  const totalSelected = useMemo(() => {
    return totalCheckboxSelected({ value: checkboxData.value, total: totalItems });
  }, [checkboxData.value, totalItems]);

  useEffect(() => {
    onTotalChange && onTotalChange(totalSelected);
  }, [onTotalChange, totalSelected]);

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <Tooltip body={t('employeeswillReceiveThis', { total: totalSelected })}>
            <div>{`${t('selected')} ${totalSelected >= 0 ? `(${totalSelected})` : ''}`}</div>
          </Tooltip>
        ),
        dataKey: 'name',
        Cell: NameRenderer,
        columnData: ({ rowData }) => ({
          withUnderline: true,
          onClick: () => setSelectedEmployee({ ...rowData })
        }),
        flexGrow: 1
      },
      {
        Header: () => <Search value={search} onChange={setSearch} keepOpen />,
        dataKey: 'total',
        minWidth: 200
      }
    ],
    [search, t, totalSelected]
  );

  const publishToNonBooked = useMemo(
    () => (filters?.basic || []).some(({ id }) => id === FILTERS_IDS.NOT_BOOKED_ON_DATE_FILTER_ID),
    [filters]
  );
  const publishToPreferredClient = useMemo(
    () => (filters?.basic || []).some(({ id }) => id === FILTERS_IDS.PREFERRED_CLIENTS_FILTER_ID),
    [filters]
  );

  const publishToWorkedInAreaBefore = useMemo(() => {
    const areaFilter = (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.WORKED_IN_AREA_BEFORE);
    return item?.locationId && Array.isArray(areaFilter?.value) && areaFilter?.value.includes(item.locationId);
  }, [filters?.basic, item.locationId]);

  const publishToPreferredArea = useMemo(() => {
    const preferredAreaFilter = (filters?.basic || []).find(({ id }) => id === FILTERS_IDS.PREFERRED_AREA);
    return (
      item?.locationId &&
      Array.isArray(preferredAreaFilter?.value) &&
      preferredAreaFilter?.value.includes(item.locationId)
    );
  }, [filters?.basic, item.locationId]);

  const availableByTimeAvailability = useMemo(
    () => (filters?.basic || []).some(({ id }) => id === FILTERS_IDS.AVAILABLE_BY_TIME_AVAILABILITY),
    [filters]
  );

  const showWorkedInAreaBefore = !!item?.locationId;
  // Preferred areas are always sub-locations, so a shift on a top-level location can never match
  const showPreferredArea = useMemo(
    () =>
      !!isEmployeePreferredAreasEnabled && !!item?.locationId && !!mappedLocations?.[item.locationId]?.isSubLocation,
    [isEmployeePreferredAreasEnabled, item.locationId, mappedLocations]
  );

  return (
    <>
      <Header>
        <FiltersRow
          selectedKey="employees"
          filters={filters}
          setFilters={setFilters}
          disabledFilters={disabledFilters}
          filterContextParams={params}
          usePortal
        />
        <Filter
          selectedKey="employees"
          filters={filters}
          setFilters={setFilters}
          disabledFilters={disabledFilters}
          usePortal
          filterContextParams={params}
        />
      </Header>
      <Container>
        <PaginationTable
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          totalItems={totalItems}
          isLoading={isLoading}
          data={data}
          columns={columns}
          checkboxData={checkboxData}
          search={search}
          pageButtonType="button"
          shadow={false}
          showCellBorder={false}
          isVirtualized
        />
      </Container>

      {projectDate && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-non-booked-one-item"
            label={t('notPublishShiftsToAlreadyBookedDuringThisDay')}
            value={publishToNonBooked}
            onChange={handleChangePublishToNonBooked}
          />
        </PublishCheckboxContainer>
      )}

      {showEarningsLimit && (
        <EarningsLimitCheckBox
          name="publish-to-not-exceeding-earnings-limit-one-item"
          value={hasEarningsLimitFilter(filters)}
          onChange={handleChangeEarningsLimit}
        />
      )}

      {showWorkedInAreaBefore && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-worked-in-area-before"
            label={t('publishToWorkedInAreaBefore')}
            value={publishToWorkedInAreaBefore}
            onChange={handleChangeWorkedInAreaBefore}
          />
        </PublishCheckboxContainer>
      )}

      {showPreferredArea && (
        <PublishCheckboxContainer>
          <CheckBox
            name="publish-to-preferred-area"
            label={t('publishToPreferredArea')}
            value={publishToPreferredArea}
            onChange={handleChangePreferredArea}
          />
        </PublishCheckboxContainer>
      )}

      {clientId && (
        <PubishToPreferred>
          <CheckBox
            name="publish-to-preferred-client-one-item"
            label={t('publishToPreferredClient')}
            value={publishToPreferredClient}
            onChange={handleChangePublishToPreferredClient}
          />
        </PubishToPreferred>
      )}

      {showCheckWithAvailableByTimeAvailability && (
        <PubishToPreferred>
          <CheckBox
            name="publish-to-available-employees"
            label={t('toggleCheckAvilabilityWithAvailableOnly')}
            value={availableByTimeAvailability}
            onChange={handleChangeAvailableByTimeAvailability}
          />
        </PubishToPreferred>
      )}

      {selectedEmployee && <EmployeeCard defaultData={selectedEmployee} onClose={() => setSelectedEmployee()} />}
    </>
  );
};

export default EmployeesContent;
